// ImessagePanel — Settings card for the iMessage BYO integration.
//
// Lives under Settings → Comms & Calendar (paired with the Kixie
// panel — same visual language). Three states:
//   · loading      — initial fetch of /api/imessage/status
//   · not_connected — form: paste Sendblue key pair + line number,
//                     accept §8 attestation, connect
//   · connected    — inbound URL, line number, status metadata,
//                    channel preference toggle, disconnect
//
// UX principles from spec §1 and §11:
//   - Never say "connect your iMessage line" without also saying
//     "your personal number stays private." Copy is load-bearing.
//   - The attestation checkbox is REAL: pastes attestedText to
//     /api/imessage/connect (which requires it to be ≥60 chars).
//   - Show the A2P prerequisite up front — no surprises about
//     Android contacts going dark.
//   - Kill any "imessage_only" mental model. The channel toggle is
//     "iMessage preferred" (SMS fallback), never "iMessage only."

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { supabase } from '../lib/supabase';

// The exact attestation text the agent must accept. Also captured
// server-side (imessage_connections.attested_text) so a future audit
// can prove the wording. Change here + re-attest all agents if the
// legal boundary shifts.
const ATTESTATION_TEXT =
  'I confirm the Sendblue iMessage line I am connecting is MINE, provisioned on my Sendblue account. I will only send messages through this CRM to contacts who have opted in. Any use of my Sendblue line outside this CRM is my sole responsibility. I understand SMS fallback for non-Apple contacts still requires A2P/10DLC registration on my Relay number.';

async function bearerHeaders() {
  try {
    const token = readPersistedJwtDirect();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch { return {}; }
}

function StatusBadge({ status }) {
  const map = {
    connected:    { bg: 'bg-emerald-100', fg: 'text-emerald-800', label: 'Connected' },
    new:          { bg: 'bg-slate-100',   fg: 'text-slate-700',   label: 'Not connected' },
    referred:     { bg: 'bg-violet-100',  fg: 'text-violet-800',  label: 'Signup in progress' },
    invalid:      { bg: 'bg-red-100',     fg: 'text-red-800',     label: 'Credentials invalid — reconnect' },
    disconnected: { bg: 'bg-slate-100',   fg: 'text-slate-600',   label: 'Disconnected' },
  };
  const s = map[status] || map.new;
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${s.bg} ${s.fg}`}>
      {s.label}
    </span>
  );
}

function ConnectForm({ onSubmit, busy, error }) {
  const [apiKeyId, setApiKeyId] = React.useState('');
  const [apiSecretKey, setApiSecretKey] = React.useState('');
  const [sendblueLineNumber, setSendblueLineNumber] = React.useState('');
  const [attested, setAttested] = React.useState(false);

  const canSubmit = apiKeyId.trim().length >= 8
                 && apiSecretKey.trim().length >= 8
                 && attested;

  const submit = (e) => {
    e.preventDefault();
    if (!canSubmit || busy) return;
    onSubmit({
      apiKeyId: apiKeyId.trim(),
      apiSecretKey: apiSecretKey.trim(),
      sendblueLineNumber: sendblueLineNumber.trim() || null,
      attestedText: ATTESTATION_TEXT,
    });
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="bg-sky-50 border border-sky-200 rounded-lg p-3">
        <div className="text-xs font-bold text-sky-900 mb-1">Before you connect</div>
        <div className="text-[11px] text-sky-800 space-y-1">
          <div>· This connects a Sendblue account you already own. We do <b>not</b> touch your personal iPhone or Apple ID.</div>
          <div>· Sendblue provisions a <b>fresh iMessage line</b> on your account — you can't port your existing business number in.</div>
          <div>· <b>A2P/10DLC on your Relay number stays required.</b> Non-Apple (Android) contacts still receive SMS from that number.</div>
        </div>
      </div>

      <label className="block">
        <div className="text-xs font-semibold text-slate-700 mb-1">Sendblue API Key ID</div>
        <input
          value={apiKeyId}
          onChange={(e) => setApiKeyId(e.target.value)}
          type="text"
          placeholder="sb-api-key-id from Sendblue → API Keys"
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"
          autoComplete="off"
          disabled={busy}
        />
      </label>

      <label className="block">
        <div className="text-xs font-semibold text-slate-700 mb-1">Sendblue API Secret Key</div>
        <input
          value={apiSecretKey}
          onChange={(e) => setApiSecretKey(e.target.value)}
          type="password"
          placeholder="sb-api-secret-key from Sendblue → API Keys"
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"
          autoComplete="new-password"
          disabled={busy}
        />
        <div className="text-[10px] text-slate-500 mt-1">
          Stored encrypted in our vault. Never returned to your browser after this paste.
        </div>
      </label>

      <label className="block">
        <div className="text-xs font-semibold text-slate-700 mb-1">
          Sendblue Line Number <span className="text-slate-400">(optional)</span>
        </div>
        <input
          value={sendblueLineNumber}
          onChange={(e) => setSendblueLineNumber(e.target.value)}
          type="tel"
          placeholder="+15551234567 (from Sendblue dashboard)"
          className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"
          disabled={busy}
        />
      </label>

      <label className="flex gap-2 items-start bg-amber-50 border border-amber-200 rounded-lg p-3 cursor-pointer">
        <input
          type="checkbox"
          checked={attested}
          onChange={(e) => setAttested(e.target.checked)}
          className="mt-0.5"
          disabled={busy}
        />
        <div className="text-[11px] text-amber-900 leading-relaxed">
          {ATTESTATION_TEXT}
        </div>
      </label>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-2 text-xs text-red-800">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!canSubmit || busy}
        className={`w-full py-2 rounded-md text-sm font-semibold ${
          canSubmit && !busy
            ? 'bg-slate-900 text-white hover:bg-slate-800'
            : 'bg-slate-200 text-slate-500 cursor-not-allowed'
        }`}
      >
        {busy ? 'Connecting…' : 'Connect Sendblue'}
      </button>
    </form>
  );
}

function ConnectedView({ state, onDisconnect, onSetPreference, busy, error }) {
  const c = state.connection;
  const preferred = state.preference === 'imessage_preferred';

  return (
    <div className="space-y-3">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs font-bold text-emerald-900">iMessage line active</div>
          <StatusBadge status={c.status}/>
        </div>
        {c.sendblue_line_number && (
          <div className="text-[11px] text-emerald-800 font-mono">
            Line: {c.sendblue_line_number}
          </div>
        )}
        {c.connected_at && (
          <div className="text-[10px] text-emerald-700">
            Connected {new Date(c.connected_at).toLocaleString()}
          </div>
        )}
      </div>

      {c.inbound_url && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
          <div className="text-xs font-semibold text-slate-700 mb-1">Inbound webhook</div>
          <div className="text-[10px] text-slate-600 font-mono break-all">
            {c.inbound_url}
          </div>
          <div className="text-[10px] text-slate-500 mt-1">
            Registered on your Sendblue account. Replies land in the CRM inbox.
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg p-3">
        <div className="text-xs font-semibold text-slate-700 mb-2">Channel preference</div>
        <div className="space-y-2">
          <label className="flex gap-2 items-start cursor-pointer">
            <input
              type="radio"
              checked={!preferred}
              onChange={() => onSetPreference('sms_only')}
              disabled={busy}
              className="mt-0.5"
            />
            <div>
              <div className="text-xs font-semibold text-slate-800">SMS only</div>
              <div className="text-[10px] text-slate-500">All outbound goes over your Relay SMS number, even to iPhone contacts.</div>
            </div>
          </label>
          <label className="flex gap-2 items-start cursor-pointer">
            <input
              type="radio"
              checked={preferred}
              onChange={() => onSetPreference('imessage_preferred')}
              disabled={busy}
              className="mt-0.5"
            />
            <div>
              <div className="text-xs font-semibold text-slate-800">iMessage preferred (SMS fallback)</div>
              <div className="text-[10px] text-slate-500">iPhone contacts receive blue-bubble iMessage. Android + non-Apple contacts fall back to your Relay SMS number automatically.</div>
            </div>
          </label>
        </div>
      </div>

      {c.status === 'invalid' && c.last_error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-2 text-xs text-red-800">
          <div className="font-semibold">Sendblue rejected the credentials.</div>
          <div className="text-[10px] mt-1">{c.last_error}</div>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-2 text-xs text-red-800">
          {error}
        </div>
      )}

      <button
        onClick={onDisconnect}
        disabled={busy}
        className="w-full py-2 rounded-md text-sm font-semibold bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
      >
        {busy ? 'Disconnecting…' : 'Disconnect'}
      </button>
    </div>
  );
}

export default function ImessagePanel() {
  const [loading, setLoading] = React.useState(true);
  const [state, setState] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await bearerHeaders();
      const res = await fetch('/api/imessage/status', { headers });
      const body = await res.json();
      if (!res.ok || !body.ok) {
        setError(body.error || `status_${res.status}`);
      } else {
        setState(body);
      }
    } catch (err) {
      setError(err.message || 'network_error');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const handleConnect = async (form) => {
    setBusy(true);
    setError(null);
    try {
      const headers = { ...(await bearerHeaders()), 'content-type': 'application/json' };
      const res = await fetch('/api/imessage/connect', {
        method: 'POST',
        headers,
        body: JSON.stringify(form),
      });
      const body = await res.json();
      if (!res.ok || !body.ok) {
        setError(body.fix || body.error || `connect_${res.status}`);
      } else {
        await refresh();
      }
    } catch (err) {
      setError(err.message || 'network_error');
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect iMessage? Outbound sends will route to SMS only, and the integration fee will stop billing.')) return;
    setBusy(true);
    setError(null);
    try {
      const headers = { ...(await bearerHeaders()), 'content-type': 'application/json' };
      const res = await fetch('/api/imessage/disconnect', {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
      });
      const body = await res.json();
      if (!res.ok || !body.ok) {
        setError(body.fix || body.error || `disconnect_${res.status}`);
      } else {
        await refresh();
      }
    } catch (err) {
      setError(err.message || 'network_error');
    } finally {
      setBusy(false);
    }
  };

  const handleSetPreference = async (preference) => {
    setBusy(true);
    setError(null);
    try {
      const headers = { ...(await bearerHeaders()), 'content-type': 'application/json' };
      const res = await fetch('/api/imessage/preference', {
        method: 'POST',
        headers,
        body: JSON.stringify({ preference }),
      });
      const body = await res.json();
      if (!res.ok || !body.ok) {
        setError(body.fix || body.error || `preference_${res.status}`);
      } else {
        await refresh();
      }
    } catch (err) {
      setError(err.message || 'network_error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="text-sm text-slate-500">Loading iMessage settings…</div>
      </div>
    );
  }

  const connected = state?.connection?.status === 'connected';
  const invalid   = state?.connection?.status === 'invalid';

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm font-bold text-slate-900">iMessage (blue bubble)</div>
        {state?.connection && <StatusBadge status={state.connection.status}/>}
      </div>
      <div className="text-[11px] text-slate-500 mb-3">
        Connect your own Sendblue account — nothing installed on your phone, your personal number stays private.
      </div>

      {(connected || invalid)
        ? <ConnectedView
            state={state}
            onDisconnect={handleDisconnect}
            onSetPreference={handleSetPreference}
            busy={busy}
            error={error}
          />
        : <ConnectForm onSubmit={handleConnect} busy={busy} error={error}/>
      }
    </div>
  );
}
